import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { CollectorType } from "providers/HostedScrapingProvider";
import { isHomeDepotSearchProject, isHomeDepotProductProject, isHomeDepotReviewsProject } from "sdecontent";

import { ApiCallConfig, ScrapingMethod, SupportedLanguages } from "routes/dashboard/ApiPlaygroundTypes";

import { useLocalStorage } from "./useLocalStorage";


// Trim whitespace-padded HD text params (dropping empties) so a value that passes validation isn't serialized as e.g. url=%20value.
export function normalizeSdeIdentifiers(apiParams: any): any {
  const result = { ...(apiParams ?? {}) };
  for (const key of ['url', 'gtin', 'page', 'minPrice', 'maxPrice']) {
    if (typeof result[key] === 'string') {
      const trimmed = result[key].trim();
      if (trimmed === '') delete result[key];
      else result[key] = trimmed;
    }
  }
  return result;
}

// Trim each line of a Home Depot primary input (dropping blanks) so a padded item_id/term isn't sent or serialized.
export function normalizeSdeInput(type: CollectorType, input: string): string {
  if (!isHomeDepotSearchProject(type) && !isHomeDepotProductProject(type) && !isHomeDepotReviewsProject(type)) {
    return input;
  }
  return input.split('\n').map((line) => line.trim()).filter((line) => line.length > 0).join('\n');
}

export function toApiCallConfig(settings: ApiPlaygroundSettings): ApiCallConfig {
  const apiParams = normalizeSdeIdentifiers(settings.apiParams);
  return {
    input: normalizeSdeInput(settings.type, settings.input),
    scrapingMethod: settings.scrapingMethod,
    config: {
      type: settings.type,
      apiParams
    }
  };
}

export type ApiPlaygroundLocationState = {
  language: SupportedLanguages;
  scrapingMethod: ScrapingMethod;
  collectorType: CollectorType;
  url: string;
};

export type ApiPlaygroundSettings = {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  scrapingMethod: ScrapingMethod;
  setScrapingMethod: Dispatch<SetStateAction<ScrapingMethod>>;
  savedScrapingMethod: ScrapingMethod,
  setSavedScrapingMethod: Dispatch<SetStateAction<ScrapingMethod>>;
  type: CollectorType;
  setType: Dispatch<SetStateAction<CollectorType>>;
  sdeType: CollectorType;
  setSdeType: Dispatch<SetStateAction<CollectorType>>;
  apiParams: any;
  setApiParams: Dispatch<SetStateAction<any>>;
  language: SupportedLanguages;
  setLanguage: Dispatch<SetStateAction<SupportedLanguages>>;
}

export default function useApiPlaygroundSettings(): ApiPlaygroundSettings {
  const locationState = useLocation().state as ApiPlaygroundLocationState | undefined;

  const [ input, setInput ] = useState<string>(locationState?.url || "");

  const [ savedScrapingMethod, setSavedScrapingMethod ] = useLocalStorage<ScrapingMethod>("apiRequestScrapingMethod", "api");
  const [ scrapingMethod, setScrapingMethod ] = useState<ScrapingMethod>(locationState?.scrapingMethod || savedScrapingMethod);
  useEffect(() => {
    setSavedScrapingMethod(scrapingMethod);
  }, [ setSavedScrapingMethod, scrapingMethod ]);

  const [ sdeType, setSdeType ] = useLocalStorage<CollectorType>("apiPlaygroundScrapingType", "async_google_search");
  const [ type, setType ] = useState<CollectorType>(locationState?.collectorType || sdeType);

  useEffect(() => {
    if (scrapingMethod === "structured_data_endpoint") {
      if (type === "async_urls") {
        // SDEs must use their own types, pick the last selected SDE instead of using 'async_urls'
        setType(sdeType);
      } else {
        // all good, save the user's choice
        setSdeType(type);
      }
    } else {
      // not using SDEs, collector type must be 'async_urls'
      setType("async_urls");
    }
  }, [ type, sdeType, scrapingMethod, setType, setSdeType ]);

  const [ apiParams, setApiParams ] = useState<any>({});

  const [ savedLanguage, setSavedLanguage ] = useLocalStorage<SupportedLanguages>("apiRequestSelectedLanguage", "python");
  const [ language, setLanguage ] = useState<SupportedLanguages>(locationState?.language || savedLanguage);
  useEffect(() => {
    setSavedLanguage(language);
  }, [ setSavedLanguage, language ]);


  return {
    input,
    setInput,
    scrapingMethod,
    setScrapingMethod,
    savedScrapingMethod,
    setSavedScrapingMethod,
    type,
    setType,
    sdeType,
    setSdeType,
    apiParams,
    setApiParams,
    language,
    setLanguage,
  };
};
